import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Residential Painting',
    description: 'Interior and exterior painting for Woodstock homes and apartments',
    price: 'From R8,000',
    features: ['Modern finishes', 'Quality materials', 'Clean work', '2-year warranty']
  },
  {
    icon: 'ri-building-line',
    title: 'Commercial Painting',
    description: 'Creative painting solutions for cafes, galleries, and offices',
    price: 'From R12,000',
    features: ['Custom colors', 'Flexible hours', 'Fast turnaround', 'Durable coatings']
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Industrial Painting',
    description: 'Specialized coatings for warehouses and industrial spaces',
    price: 'From R15,000',
    features: ['Heavy-duty coatings', 'Safety compliance', 'Large-scale projects', 'Long-lasting finish']
  }
];

const paintingProcess = [
  { step: '1', title: 'Consultation', description: 'Site visit and project discussion' },
  { step: '2', title: 'Preparation', description: 'Surface prep and area protection' },
  { step: '3', title: 'Execution', description: 'Professional painting with quality materials' },
  { step: '4', title: 'Handover', description: 'Final inspection and project completion' }
];

const testimonials = [
  {
    rating: 5,
    text: 'Fantastic work on our Woodstock loft! The team understood our vision and delivered a perfect finish. Professional, creative, and reliable.',
    name: 'James Mitchell',
    property: 'Woodstock Loft Owner',
    image: 'https://public.readdy.ai/ai/img_res/aaaa856893d67fbc5fe34c7c68131ad5.jpg'
  },
  {
    rating: 5,
    text: 'Excellent service for our cafe renovation. They worked efficiently around our schedule and the results are stunning. Highly recommend!',
    name: 'Sophie Anderson',
    property: 'Cafe Owner',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20woman%20business%20owner%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-woodstock-002&orientation=squarish'
  },
  {
    rating: 5,
    text: 'Great experience! The painters were skilled, respectful, and delivered high-quality work. Our Victorian home looks beautiful.',
    name: 'Robert Davies',
    property: 'Woodstock Resident',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20man%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-woodstock-003&orientation=squarish'
  }
];

export default function WoodstockPage() {
  useEffect(() => {
    document.title = 'Woodstock Painting Services | Professional Painters Cape Town';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services in Woodstock, Cape Town. Residential, commercial, and industrial painting solutions. Professional painters with 15+ years experience. Free quotes available.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Woodstock painters, painting services Woodstock, house painters Woodstock, commercial painters Cape Town, industrial painting');
    }

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (metaGeoPosition) {
      metaGeoPosition.setAttribute('content', '-33.9258;18.4467');
    }

    const metaGeoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (metaGeoPlacename) {
      metaGeoPlacename.setAttribute('content', 'Woodstock, Cape Town, Western Cape, South Africa');
    }

    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (metaGeoRegion) {
      metaGeoRegion.setAttribute('content', 'ZA-WC');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20urban%20building%20in%20Woodstock%20Cape%20Town%2C%20skilled%20painters%20with%20equipment%20applying%20paint%20to%20property%20exterior%2C%20trendy%20urban%20area%2C%20sunny%20day%2C%20professional%20service%2C%20high%20quality%20workmanship%2C%20realistic%20photography&width=1920&height=1080&seq=woodstock-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Woodstock
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Woodstock's residential and commercial properties. Quality craftsmanship and reliable service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Area Description */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Painting Experts Serving Woodstock</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Woodstock is Cape Town's creative hub, known for its artistic community, trendy galleries, converted warehouses, and vibrant street culture. Our professional painting services cater to the unique character of Woodstock painting services, from historic Victorian homes to modern loft apartments and commercial creative spaces.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                With over 15 years of experience serving Woodstock and the City Bowl, we understand the diverse painting needs of this dynamic neighborhood. From residential renovations to commercial fit-outs, industrial spaces to artistic projects, our team delivers exceptional results that complement Woodstock's creative spirit.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you need interior painting for your Woodstock apartment, exterior painting for commercial buildings, specialized industrial coatings, or creative decorative finishes, our skilled painters combine technical expertise with artistic sensibility to bring your vision to life.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20team%20working%20on%20urban%20building%20exterior%20in%20Woodstock%20Cape%20Town%2C%20showing%20skilled%20craftsmen%20applying%20paint%20to%20renovated%20industrial%20building%2C%20modern%20urban%20architecture%2C%20quality%20painting%20equipment%2C%20natural%20daylight%2C%20professional%20service%20photography&width=600&height=500&seq=woodstock-team-002&orientation=portrait"
                alt="Professional Painters in Woodstock"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Woodstock Painting Services</h2>
            <p className="text-xl text-gray-600">Versatile painting solutions for urban living and working spaces</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-black font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line mr-2" style={{color: '#ffff00'}}></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Woodstock Residents Choose Us</h2>
            <p className="text-xl text-gray-600">Trusted painting professionals in Cape Town's creative hub</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">15+ Years Experience</h3>
              <p className="text-gray-600">Extensive experience in Woodstock and City Bowl areas</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-palette-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Creative Solutions</h3>
              <p className="text-gray-600">Artistic approach to painting projects</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Skilled Team</h3>
              <p className="text-gray-600">Professional painters with diverse expertise</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-time-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600">Work around your business or lifestyle needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Painting Process</h2>
            <p className="text-xl text-gray-600">Efficient and professional from start to finish</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Recent Projects in Woodstock</h2>
            <p className="text-xl text-gray-600">Quality work across diverse Woodstock properties</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20loft%20apartment%20interior%20with%20freshly%20painted%20walls%20in%20Woodstock%20Cape%20Town%2C%20showing%20industrial-chic%20design%2C%20exposed%20brick%2C%20contemporary%20furniture%2C%20professional%20interior%20painting%2C%20natural%20lighting%20through%20large%20windows&width=400&height=300&seq=woodstock-project-001&orientation=landscape"
                  alt="Loft Apartment Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Loft Apartment</h3>
              <p className="text-gray-600">Modern interior painting with industrial aesthetic</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Trendy%20cafe%20or%20restaurant%20interior%20with%20vibrant%20painted%20walls%20in%20Woodstock%2C%20showing%20creative%20color%20scheme%2C%20modern%20commercial%20design%2C%20professional%20painting%20finish%2C%20ambient%20lighting%2C%20artistic%20atmosphere&width=400&height=300&seq=woodstock-project-002&orientation=landscape"
                  alt="Commercial Space Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Creative Cafe Space</h3>
              <p className="text-gray-600">Vibrant commercial painting for trendy venue</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Victorian%20house%20exterior%20with%20restored%20painted%20facade%20in%20Woodstock%20Cape%20Town%2C%20showing%20historic%20architecture%2C%20fresh%20paint%20in%20heritage%20colors%2C%20renovated%20building%2C%20professional%20restoration%20work%2C%20sunny%20day&width=400&height=300&seq=woodstock-project-003&orientation=landscape"
                  alt="Victorian Home Restoration"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Victorian Restoration</h3>
              <p className="text-gray-600">Heritage home exterior painting and restoration</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Converted%20warehouse%20office%20space%20with%20freshly%20painted%20walls%20in%20Woodstock%2C%20showing%20modern%20industrial%20design%2C%20open%20plan%20layout%2C%20professional%20commercial%20painting%2C%20natural%20light%2C%20creative%20workspace&width=400&height=300&seq=woodstock-project-004&orientation=landscape"
                  alt="Warehouse Conversion"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Warehouse Office</h3>
              <p className="text-gray-600">Industrial space conversion with modern finishes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Woodstock Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by Woodstock's creative community</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{color: '#ffff00'}}></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#ffff00'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Woodstock Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Woodstock's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}